import type { Column } from "@tanstack/react-table";
import { ArrowUpDown } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { DataTableButton } from "@/components/ui/data-table-button";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import {
  METRIC_TOOLTIP_ARROW_CLASS_NAME,
  METRIC_TOOLTIP_BODY_CLASS,
  metricTooltipShellClassName,
} from "@/components/ui/metric-tooltip";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export function fmtPct(v: number | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v.toFixed(2)}%`;
}

export function fmtNum(v: number | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  if (abs >= 1000) return v.toFixed(0);
  if (abs >= 100) return v.toFixed(1);
  return v.toFixed(2);
}

export function fmtPnl(v: number | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toFixed(2);
}

export function fmtInt(v: number | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return String(Math.round(v));
}

export function fmtConfidence(v: number | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toFixed(4);
}

/** Light/dark-aware tooltip shell for Market Context tables (matches metric tooltips). */
export function MarketContextTooltipContent({
  children,
  className,
  widthClass = "max-w-[360px]",
  side = "top",
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof TooltipContent> & { widthClass?: string }): React.ReactElement {
  return (
    <TooltipContent
      side={side}
      sideOffset={sideOffset}
      arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
      // Above workspace sheets / sticky table chrome (dropdowns use z-[1200]).
      className={cn(metricTooltipShellClassName(widthClass), "z-[1200]", className)}
      {...props}
    >
      {typeof children === "string" ? (
        <p className={`text-[11px] leading-snug ${METRIC_TOOLTIP_BODY_CLASS}`}>{children}</p>
      ) : (
        children
      )}
    </TooltipContent>
  );
}

export function MarketContextMetricColumnHeader<TData>({
  column,
  title,
  tooltip,
  align = "right",
}: {
  column: Column<TData, unknown>;
  title: string;
  tooltip?: string;
  align?: "left" | "right";
}): React.ReactElement {
  if (!tooltip) {
    return <DataTableColumnHeader<TData, unknown> column={column} title={title} align={align} />;
  }
  return (
    <TooltipProvider delayDuration={350}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DataTableButton
            variant="ghost"
            size="sm"
            data-trade-log-column-header=""
            data-column-header-align={align === "right" ? "right" : undefined}
            className="h-8 max-h-8 min-h-8 gap-2 px-3"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <span className="border-b border-dotted border-current">{title}</span>
            <ArrowUpDown className="size-4 shrink-0 text-[color:var(--brand-tropical-surf)]" />
          </DataTableButton>
        </TooltipTrigger>
        <MarketContextTooltipContent widthClass="max-w-[280px]">{tooltip}</MarketContextTooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Slice / regime summary sub-tables — Open-into-configurator column title (not “Actions”). */
export const DRILLDOWN_COLUMN_TITLE = "Drilldown";

/** Left-aligned shell for Add to Simulator / Drilldown headers (nested under tooltip triggers). */
export function MarketContextActionColumnHeader({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      data-market-context-action-column-header=""
      className="flex h-8 max-h-8 min-h-8 w-full min-w-0 items-center justify-start font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]"
    >
      {children}
    </div>
  );
}

export function MarketContextActionColumnCell({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      data-market-context-action-column-cell=""
      className="flex w-full min-w-0 items-center justify-start py-1"
    >
      {children}
    </div>
  );
}

export function AddToSimulatorColumnHeader({
  compact = false,
}: {
  /** Narrow header for dense tables (full label stays in tooltip). */
  compact?: boolean;
}): React.ReactElement {
  if (!compact) {
    return (
      <MarketContextActionColumnHeader>Add to Simulator</MarketContextActionColumnHeader>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex max-w-full">
            <MarketContextActionColumnHeader>
              <span className="cursor-help border-b border-dotted border-current">Add</span>
            </MarketContextActionColumnHeader>
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          sideOffset={4}
          arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
          className={metricTooltipShellClassName("max-w-[220px]")}
        >
          <p className={`text-[11px] leading-snug ${METRIC_TOOLTIP_BODY_CLASS}`}>
            Add this slice to the Execution Rules Simulator pick list.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function DrilldownColumnHeader({
  dottedUnderline = false,
}: {
  dottedUnderline?: boolean;
}): React.ReactElement {
  return (
    <MarketContextActionColumnHeader>
      <span
        className={
          dottedUnderline ? "cursor-help border-b border-dotted border-current" : undefined
        }
      >
        {DRILLDOWN_COLUMN_TITLE}
      </span>
    </MarketContextActionColumnHeader>
  );
}

export function MarketContextPreviewTradesColumnHeader(): React.ReactElement {
  return (
    <div className="flex h-8 items-center font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">
      Preview
    </div>
  );
}

export function MarketContextPreviewTradesButton({
  rowKey,
  activeKey,
  loadingKey,
  disabled,
  onClick,
}: {
  rowKey: string;
  activeKey?: string | readonly string[];
  loadingKey?: string | readonly string[];
  disabled?: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}): React.ReactElement {
  const isActive = marketContextPreviewKeyMatches(activeKey, rowKey);
  const isLoading = marketContextPreviewKeyMatches(loadingKey, rowKey);
  const isDisabled = Boolean(disabled) || isLoading;

  return (
    <MarketContextActionColumnCell>
      <Button
        type="button"
        variant={isActive && !isLoading ? "gradient-green" : "gradient"}
        size="sm"
        data-font="primary"
        data-market-context-preview-active={isActive && !isLoading ? "" : undefined}
        className={cn(
          // Fixed width avoids Preview ↔ Active label shift; brightness hover
          // (not opacity) keeps the fill opaque over table row :hover.
          "h-8 min-w-[5.75rem] shrink-0 px-4 transition-[filter] active:translate-y-0",
          "hover:!opacity-100 hover:brightness-[0.96] active:brightness-[0.92]",
        )}
        disabled={isDisabled}
        onClick={onClick}
      >
        <span className="uppercase tracking-wide">
          {isLoading ? "…" : isActive ? "Active" : "Preview"}
        </span>
      </Button>
    </MarketContextActionColumnCell>
  );
}

/** True when `rowKey` is the active/loading preview selection (single key or multi). */
export function marketContextPreviewKeyMatches(
  selection: string | readonly string[] | undefined,
  rowKey: string,
): boolean {
  if (!selection || !rowKey) return false;
  return typeof selection === "string" ? selection === rowKey : selection.includes(rowKey);
}
